import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import { useToast } from '../../components/common/ToastContainer';
import configTemplateService from '../../services/configTemplate.service';
import type { ConfigTemplate } from '../../services/configTemplate.service';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useTenantStore } from '../../stores/tenantStore';

const ConfigTemplates = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { selectedTenantId } = useTenantStore();
  const [templates, setTemplates] = useState<ConfigTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; template?: ConfigTemplate }>({ show: false });
  const [duplicateConfirm, setDuplicateConfirm] = useState<{ show: boolean; template?: ConfigTemplate }>({ show: false });
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templateList = await configTemplateService.list({
        edgeModel: 'VSR1000',
        ...(selectedTenantId && { tenantId: selectedTenantId }),
      });
      setTemplates(templateList || []);
    } catch (error: any) {
      console.error('Failed to load templates:', error);
      toast.error('Load Failed', error.message || 'Failed to load configuration templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [selectedTenantId]);

  const handleDelete = async () => {
    if (!deleteConfirm.template) return;

    try {
      setDeleting(true);
      await configTemplateService.delete(deleteConfirm.template.id);
      toast.success('Template Deleted', `Template "${deleteConfirm.template.name}" has been deleted`);
      setDeleteConfirm({ show: false });
      await loadTemplates();
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      toast.error('Delete Failed', error.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (template: ConfigTemplate) => {
    try {
      setDuplicating(true);
      const newName = `${template.name} (Copy)`;
      await configTemplateService.duplicate(template.id, newName);
      toast.success('Template Duplicated', `Template "${newName}" has been created`);
      setDuplicateConfirm({ show: false });
      await loadTemplates();
    } catch (error: any) {
      console.error('Failed to duplicate template:', error);
      toast.error('Duplicate Failed', error.message || 'Failed to duplicate template');
    } finally {
      setDuplicating(false);
    }
  };

  const columns: Column<ConfigTemplate>[] = [
    {
      key: 'name',
      label: 'Template Name',
      sortable: true,
      render: (template) => (
        <div>
          <button
            onClick={() => navigate(`/admin/config-templates/${template.id}`)}
            className="font-medium text-left hover:text-primary-600 transition-colors"
          >
            {template.name}
          </button>
          {template.description && (
            <div className="text-sm text-gray-500 mt-1">{template.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'edge_model',
      label: 'Edge Model',
      sortable: true,
      render: (template) => template.edge_model || 'VSR1000',
    },
    {
      key: 'is_default',
      label: 'Default',
      render: (template) => (
        template.is_default ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Default
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      key: 'is_system',
      label: 'Type',
      render: (template) => (
        template.is_system ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            System
          </span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Custom
          </span>
        )
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (template) => (
        <div className="flex items-center space-x-2">
          {canCreate && (
          <button
            onClick={() => setDuplicateConfirm({ show: true, template })}
            className="p-1.5 hover:bg-blue-50 rounded transition-colors"
            title="Duplicate Template"
          >
            <Copy className="w-4 h-4 text-blue-600 hover:text-blue-700" />
          </button>
          )}
          {!template.is_system && (
            <>
          {canUpdate && (
          <button
            onClick={() => navigate(`/admin/config-templates/${template.id}`)}
            className="p-1.5 hover:bg-gray-50 rounded transition-colors"
            title="Edit Template"
          >
            <Edit className="w-4 h-4 text-gray-600 hover:text-gray-700" />
          </button>
          )}
              {canDelete && (
              <button
                onClick={() => setDeleteConfirm({ show: true, template })}
                className="p-1.5 hover:bg-red-50 rounded transition-colors"
                title="Delete Template"
              >
                <Trash2 className="w-4 h-4 text-red-600 hover:text-red-700" />
              </button>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuration Templates</h1>
          <p className="text-gray-600 mt-1">Manage configuration templates for edge device provisioning</p>
        </div>
        {canCreate && (
        <button
          onClick={() => navigate('/admin/config-templates/create')}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Template</span>
        </button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={templates}
        loading={loading}
        emptyMessage="No configuration templates found"
      />

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Configuration Template"
        message={`Are you sure you want to delete template "${deleteConfirm.template?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />

      <ConfirmDialog
        isOpen={duplicateConfirm.show}
        title="Duplicate Template"
        message={`Create a copy of "${duplicateConfirm.template?.name}"?`}
        confirmText="Duplicate"
        variant="info"
        onConfirm={() => {
          if (duplicateConfirm.template) {
            handleDuplicate(duplicateConfirm.template);
          }
        }}
        onClose={() => setDuplicateConfirm({ show: false })}
        isLoading={duplicating}
      />
    </div>
  );
};

export default ConfigTemplates;

